import { all, call, put, race, select, spawn, take } from 'redux-saga/effects';
import { accountDetailsRequest, updateSavedSlipsCount } from '../../modules/account/actions';
import { getNumberOfSavedSlips } from '../../modules/account/selectors';
import { serviceUnavailable } from '../../modules/app/actions';
import {
  ConnectionError,
  NotFoundError,
  ServiceUnavailableError,
  UnauthorizedAccessError
} from '../../modules/app/errors';
import { restartUserJourney } from '../../modules/auth/saga';
import { loadEvents } from '../../modules/events/saga';
import { close, open } from '../../modules/modal/actions';
import { isAuthenticated } from '../../modules/user/selectors';
import * as notify from '../layout/Notifications/actions';
import {
  DELETE_SLIP,
  loadSlipsFailed,
  loadSlipsStart,
  loadSlipsSuccess,
  SAVE_SLIP_BUTTON_CLICKED,
  SAVED_SLIPS_VIEW_ENTER,
  SET_EVENTS_DAY,
  SET_MARKET_GROUP_ID,
  SLIP_DELETE_CANCELLED,
  SLIP_DELETE_CONFIRMED,
  SLIP_SELECTION_ADD,
  slipSaved,
  slipSavePending,
  VIEW_ENTER
} from './actions';
import { SaveSlipConflictError } from './errors';
import { deleteSlip, fetchSlips, saveSlip } from './rest';
import { getEventsDay, getMarketGroupId, getSlipToSave, isLegsLimitExceeded } from './selectors';

export function* watchEventsViewEnter() {
  while (yield take([VIEW_ENTER, SET_EVENTS_DAY, SET_MARKET_GROUP_ID])) {
    const day = yield select(getEventsDay);
    const marketGroupId = yield select(getMarketGroupId);
    yield call(loadEvents, day, false, marketGroupId);
    const authenticated = yield select(isAuthenticated);
    if (authenticated) {
      yield put(accountDetailsRequest());
    }
  }
}

export function* watchLegsLimit() {
  while (yield take(SLIP_SELECTION_ADD)) {
    const legsLimitExceeded = yield select(isLegsLimitExceeded);
    if (legsLimitExceeded) {
      yield put(notify.add('Maximum of 20 selections allowed.', notify.ERROR));
    }
  }
}

export function* loadSlips() {
  try {
    yield put(loadSlipsStart());
    const slips = yield call(fetchSlips);
    yield put(loadSlipsSuccess(slips));
  } catch (e) {
    yield put(loadSlipsFailed());
    if (e instanceof ConnectionError) {
      yield put(notify.add('Connection error.', notify.ERROR));
    } else if (e instanceof UnauthorizedAccessError) {
      yield call(restartUserJourney);
    } else if (e instanceof ServiceUnavailableError) {
      yield put(serviceUnavailable());
    } else {
      throw e;
    }
  }
}

export function* watchSlipsViewEnter() {
  while (yield take(SAVED_SLIPS_VIEW_ENTER)) {
    yield call(loadSlips);
  }
}

export function* watchSaveSlip() {
  while (yield take(SAVE_SLIP_BUTTON_CLICKED)) {
    yield put(slipSavePending(true));
    const slip = yield select(getSlipToSave);
    try {
      yield call(saveSlip, null, slip);
      yield put(slipSaved());
      yield put(updateSavedSlipsCount(1));
      yield put(open({ componentName: 'CompleteYourBet' }));
    } catch (e) {
      if (e instanceof ConnectionError || e instanceof NotFoundError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (e instanceof SaveSlipConflictError) {
        yield put(open({ componentName: 'SavedSlipsLimitReached' }));
      } else if (e instanceof UnauthorizedAccessError) {
        yield call(restartUserJourney);
      } else if (e instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else {
        throw e;
      }
    } finally {
      yield put(slipSavePending(false));
    }
  }
}

export function* watchDeleteSlip() {
  while (true) {
    const { slipId } = yield take(DELETE_SLIP);
    try {
      yield put(open({ componentName: 'SlipDeleteConfirmationModal' }));
      const { confirmed } = yield race({
        confirmed: take(SLIP_DELETE_CONFIRMED),
        cancelled: take(SLIP_DELETE_CANCELLED)
      });
      yield put(close());
      const numberOfSavedSlips = yield select(getNumberOfSavedSlips);

      if (confirmed && numberOfSavedSlips > 1) {
        yield put(open({ componentName: 'RequestInProgress' }));
        yield call(deleteSlip, slipId);
        yield put(close());
        yield call(loadSlips);
        yield put(notify.add('Slip successfully deleted', notify.INFO));
      } else if (confirmed && numberOfSavedSlips === 1) {
        yield call(deleteSlip, slipId);
        yield put(updateSavedSlipsCount(-1));
        yield put(notify.add('Slip successfully deleted', notify.INFO));
      }
    } catch (e) {
      yield put(close());
      if (e instanceof NotFoundError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else {
        throw e;
      }
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchEventsViewEnter),
    spawn(watchLegsLimit),
    spawn(watchSlipsViewEnter),
    spawn(watchSaveSlip),
    spawn(watchDeleteSlip)
  ]);
}
/* eslint-enable no-constant-condition */
